/**
 * Trap Panel
 * Displays current trap status and controls: set, soak timer, pull, cooldown.
 * Shown in FishingView sidebar (desktop) and inline (mobile).
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useGameStore } from '@/state';
import { trapManager } from '@/systems';
import {
  TrapStatus,
  EquipmentSlot,
  GameEvent,
} from '@/types';
import { EQUIPMENT_DEFINITIONS, getEquipmentDefinition } from '@/data';
import { eventBus } from '@/services/EventBus';

/**
 * Format milliseconds into M:SS display
 */
function formatCountdown(ms: number): string {
  if (ms <= 0) return '0:00';
  const totalSeconds = Math.ceil(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function TrapPanel() {
  const trapState = useGameStore((s) => s.playerState.trapState);
  const ownedEquipment = useGameStore((s) => s.playerState.ownedEquipment);
  const equippedGear = useGameStore((s) => s.playerState.equippedGear);
  const inventory = useGameStore((s) => s.playerState.inventory);

  const [timeRemaining, setTimeRemaining] = useState(0);

  // Get the equipped trap definition
  const equippedTrapId = equippedGear[EquipmentSlot.TRAP] ?? null;
  const equippedTrap = useMemo(() => {
    if (!equippedTrapId) return null;
    try {
      const def = getEquipmentDefinition(equippedTrapId);
      // Trap bait in the TRAP slot doesn't count as an equipped trap
      if (def.isTrapBait) return null;
      return def;
    } catch {
      return null;
    }
  }, [equippedTrapId]);

  // Check if player owns any trap at all
  const ownsAnyTrap = useMemo(() => {
    return EQUIPMENT_DEFINITIONS.some(
      (eq) =>
        eq.slot === EquipmentSlot.TRAP &&
        !eq.isTrapBait &&
        ownedEquipment[eq.id]?.quantity > 0,
    );
  }, [ownedEquipment]);

  // Get available trap bait items (owned, consumable, isTrapBait)
  const availableBait = useMemo(() => {
    return EQUIPMENT_DEFINITIONS.filter(
      (eq) => eq.isTrapBait && ownedEquipment[eq.id]?.quantity > 0,
    );
  }, [ownedEquipment]);

  // Countdown timer — updates every second
  useEffect(() => {
    const updateTimer = () => {
      const now = Date.now();

      if (trapState.status === TrapStatus.SOAKING && trapState.readyAt) {
        setTimeRemaining(Math.max(0, trapState.readyAt - now));
      } else if (trapState.status === TrapStatus.COOLDOWN && trapState.cooldownEndsAt) {
        setTimeRemaining(Math.max(0, trapState.cooldownEndsAt - now));
      } else {
        setTimeRemaining(0);
      }
    };

    updateTimer();

    if (
      trapState.status === TrapStatus.SOAKING ||
      trapState.status === TrapStatus.COOLDOWN
    ) {
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [trapState.status, trapState.readyAt, trapState.cooldownEndsAt]);

  // Listen for TRAP_READY to force re-render when soak completes
  useEffect(() => {
    const unsub = eventBus.on(GameEvent.TRAP_READY, () => {
      setTimeRemaining(0);
    });
    return unsub;
  }, []);

  // Reactive canSetTrap — re-evaluates whenever subscribed state changes
  const canSetTrap = useMemo(() => {
    if (trapState.status !== TrapStatus.EMPTY) return false;
    if (!equippedTrap) return false; // No trap equipped or bait in slot
    if (inventory.currentCount >= inventory.capacity) return false;
    return true;
  }, [trapState.status, equippedTrap, inventory.currentCount, inventory.capacity]);

  const handleSetTrap = useCallback(() => {
    const success = trapManager.setTrap();
    if (!success) {
      console.warn('🪤 Set trap failed — check console for details');
    }
  }, []);

  const handlePullTrap = useCallback(() => {
    trapManager.pullTrap();
  }, []);

  const handleBaitChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    trapManager.selectTrapBait(value === '' ? null : value);
  }, []);

  const handleNavigateToOutfitter = useCallback(() => {
    useGameStore.setState((draft) => {
      draft.currentTab = 'outfitters';
    });
  }, []);

  // --- No trap owned state ---
  if (!ownsAnyTrap) {
    return (
      <div className="p-3 space-y-2">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1 flex items-center gap-1.5">
          <span>Trap</span>
        </h3>
        <div className="bg-gray-800/60 rounded-lg p-4 border border-gray-700 text-center space-y-2">
          <p className="text-sm text-gray-400">
            Buy a trap at the Outfitter to get started!
          </p>
          <button
            onClick={handleNavigateToOutfitter}
            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors"
          >
            Visit Outfitter
          </button>
        </div>
      </div>
    );
  }

  // --- Progress bar calculation (soaking state) ---
  const progressPercent =
    trapState.status === TrapStatus.SOAKING && trapState.soakDurationMs > 0
      ? Math.min(100, (1 - timeRemaining / trapState.soakDurationMs) * 100)
      : 0;

  // Yield range display
  const yieldMin = equippedTrap?.trapYieldMin ?? 0;
  const yieldMax = equippedTrap?.trapYieldMax ?? 0;

  return (
    <div className="p-3 space-y-2">
      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1 flex items-center gap-1.5">
        <span>Trap</span>
      </h3>

      <div className="bg-gray-800/60 rounded-lg p-3 border border-gray-700 space-y-3">
        {/* Trap name and tier */}
        {equippedTrap && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-base">{equippedTrap.icon ?? '🪤'}</span>
              <div>
                <span className="text-sm font-medium text-gray-200">
                  {equippedTrap.name}
                </span>
                <span className="text-[10px] text-gray-500 ml-1.5">
                  Tier {equippedTrap.tier}
                </span>
              </div>
            </div>
            {yieldMin > 0 && (
              <span className="text-[10px] text-gray-400">
                {yieldMin}-{yieldMax} creatures
              </span>
            )}
          </div>
        )}

        {/* Bait selector — only show when EMPTY or READY */}
        {(trapState.status === TrapStatus.EMPTY ||
          trapState.status === TrapStatus.READY) && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 shrink-0">Bait:</label>
            <select
              value={trapState.selectedTrapBait ?? ''}
              onChange={handleBaitChange}
              className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-xs text-gray-200 focus:outline-none focus:border-blue-500 transition-colors"
            >
              <option value="">None</option>
              {availableBait.map((bait) => (
                <option key={bait.id} value={bait.id}>
                  {bait.icon ?? ''} {bait.name} ({ownedEquipment[bait.id]?.quantity ?? 0})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Soaking state — progress bar + timer */}
        {trapState.status === TrapStatus.SOAKING && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
                </span>
                <span className="text-xs text-blue-300">Soaking...</span>
              </div>
              <span className="text-xs font-mono text-gray-300">
                {formatCountdown(timeRemaining)}
              </span>
            </div>
            <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all duration-1000"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        )}

        {/* Ready state — call to action */}
        {trapState.status === TrapStatus.READY && (
          <div className="text-center">
            <p className="text-xs text-yellow-300 mb-1">Your trap is ready!</p>
          </div>
        )}

        {/* Cooldown state — countdown */}
        {trapState.status === TrapStatus.COOLDOWN && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Cooling down...</span>
            <span className="text-xs font-mono text-gray-400">
              {formatCountdown(timeRemaining)}
            </span>
          </div>
        )}

        {/* Action button */}
        <div>
          {trapState.status === TrapStatus.EMPTY && (
            <button
              onClick={handleSetTrap}
              disabled={!canSetTrap}
              className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors min-h-[40px]"
            >
              {!equippedTrap ? 'Equip a Trap First' : 'Set Trap'}
            </button>
          )}

          {trapState.status === TrapStatus.READY && (
            <button
              onClick={handlePullTrap}
              className="w-full px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-black text-sm font-bold rounded-lg transition-all duration-300 min-h-[40px] animate-pulse shadow-lg shadow-yellow-500/20"
            >
              Pull Trap!
            </button>
          )}

          {trapState.status === TrapStatus.COOLDOWN && (
            <button
              disabled
              className="w-full px-4 py-2 bg-gray-700 text-gray-500 text-sm font-medium rounded-lg cursor-not-allowed min-h-[40px] opacity-60"
            >
              Cooling Down...
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
